import { angleDifference, linePoint } from "./geo";
import type { PlannedRoute, Scenario, ScenarioOptions, SimulationStep, VisualFeature } from "./types";

function randomGenerator(seed: number) {
    let value = seed >>> 0;
    return () => {
        value += 0x6D2B79F5;
        let n = value; n = Math.imul(n ^ n >>> 15, n | 1); n ^= n + Math.imul(n ^ n >>> 7, n | 61);
        return ((n ^ n >>> 14) >>> 0) / 4294967296;
    };
}

export function truthAt(route: PlannedRoute, meters: number): SimulationStep["truth"] {
    let remaining = meters;
    for (let index = 0; index < route.legs.length; index++) {
        const leg = route.legs[index];
        if (remaining <= leg.lengthMeters || index === route.legs.length - 1) {
            return { coordinate: linePoint(leg.coordinates, leg.lengthMeters > 0 ? remaining / leg.lengthMeters : 0), heading: leg.heading, distanceAlong: meters };
        }
        remaining -= leg.lengthMeters;
    }
    throw new Error("Маршрут не містить ділянок.");
}

export function buildScenario(route: PlannedRoute, options: ScenarioOptions): Scenario {
    if (!Number.isFinite(options.speedKmh) || options.speedKmh < 5 || options.speedKmh > 100 ||
        !Number.isFinite(options.distanceNoisePercent) || options.distanceNoisePercent < 0 || options.distanceNoisePercent > 50 ||
        !Number.isInteger(options.seed) || options.seed < 0 || options.seed > 4294967295) throw new Error("Некоректні параметри симуляції.");
    const random = randomGenerator(options.seed);
    const events: { distance: number; features: VisualFeature[]; annotation: SimulationStep["annotation"] }[] = [];
    let omitted = 0, afterMissing = false;
    route.events.forEach((event, index) => {
        if ((options.noise === "missing" || options.noise === "mixed") && index === 2 && route.events.length >= 4) {
            omitted++; afterMissing = true; return;
        }
        const wrong = (options.noise === "wrong" || options.noise === "mixed") && index === 3 && route.events.length >= 5;
        events.push({ distance: event.distanceAlong,
            features: wrong ? [{ kind: event.features[0].kind === "traffic_light" ? "speed_bump" : "traffic_light", signCode: "", signValue: "" }] : event.features,
            annotation: wrong ? "wrong" : afterMissing ? "after-missing" : "normal" });
        afterMissing = false;
        if ((options.noise === "extra" || options.noise === "mixed") && index === 1 && route.events[index + 1]) {
            events.push({ distance: (event.distanceAlong + route.events[index + 1].distanceAlong) / 2,
                features: [{ kind: "road_sign", signCode: "2.3", signValue: "" }], annotation: "extra" });
        }
    });
    let previousMeters = 0, previousHeading = route.events[0].heading;
    const steps: SimulationStep[] = [];
    for (const [index, event] of events.entries()) {
        const truth = truthAt(route, event.distance);
        const delta = event.distance - previousMeters;
        const sigma = Math.max(5, delta * options.distanceNoisePercent / 100);
        const jitter = options.distanceNoisePercent === 0 ? 0 : (random() + random() + random() - 1.5) * 2 * sigma;
        const observation: SimulationStep["observation"] = {
            trackId: `track-${options.seed}-${index}`, timestampMs: Math.round(event.distance / (options.speedKmh / 3.6) * 1000),
            features: event.features.map(feature => ({ ...feature, confidence: 0.95 })),
            distance: options.useDistance && index > 0 ? { meters: Math.max(0, delta + jitter), sigmaMeters: sigma } : null,
            courseChange: options.useCourse && index > 0 ? { degrees: angleDifference(truth.heading, previousHeading), sigmaDegrees: 15 } : null,
        };
        steps.push({ observation, truth, annotation: event.annotation });
        if (options.noise === "duplicate" && index === 2) steps.push({ observation: { ...observation, timestampMs: observation.timestampMs + 50,
            distance: options.useDistance ? { meters: 0, sigmaMeters: 5 } : null,
            courseChange: options.useCourse ? { degrees: 0, sigmaDegrees: 15 } : null }, truth, annotation: "duplicate" });
        previousMeters = event.distance; previousHeading = truth.heading;
    }
    return { steps, omitted };
}
